import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { FC } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { TextAUnderlineIcon } from '@phosphor-icons/react';

import { changeCompose } from 'mastodon/actions/compose';
import { IconButton } from 'mastodon/components/icon_button';
import { LoadingIndicator } from 'mastodon/components/loading_indicator';
import { useAppDispatch, useAppSelector } from 'mastodon/store';
import { replaceWord } from 'mastodon/utils/korean_spellcheck';
import type { SpellingIssue } from 'mastodon/utils/korean_spellcheck';
import { checkKoreanSpelling } from 'mastodon/utils/korean_spellcheck_client';

import classes from './spellcheck.module.scss';

const messages = defineMessages({
  check: { id: 'spellcheck.button', defaultMessage: 'Check spelling' },
  close: { id: 'spellcheck.close', defaultMessage: 'Close spelling check' },
});

type SpellcheckState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'done'; issues: SpellingIssue[]; checkedText: string };

let state: SpellcheckState = { status: 'idle' };
// Every check or close gets a new number, so a result that arrives after the
// panel was closed or another check was started is thrown away
let currentRequest = 0;
const listeners = new Set<() => void>();

function setState(next: SpellcheckState) {
  state = next;
  listeners.forEach((listener) => {
    listener();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

const useSpellcheckState = () => useSyncExternalStore(subscribe, () => state);

async function runSpellcheck(text: string) {
  const request = ++currentRequest;
  setState({ status: 'loading' });

  try {
    // The checker and its dictionary are only downloaded the first time
    // someone checks their spelling, and run in a Web Worker
    const issues = await checkKoreanSpelling(text);

    if (request !== currentRequest) return;

    setState({ status: 'done', issues, checkedText: text });
  } catch (error) {
    if (request !== currentRequest) return;

    console.error('Spellcheck failed', error);
    setState({ status: 'error' });
  }
}

function closeSpellcheck() {
  currentRequest++;
  setState({ status: 'idle' });
}

const useComposeText = () =>
  useAppSelector((s) => s.compose.get('text') as string);

/** Toolbar button that checks the spelling of the post being written */
export const SpellcheckButton: FC = () => {
  const intl = useIntl();
  const text = useComposeText();
  const current = useSpellcheckState();
  const open = current.status !== 'idle';

  const handleClick = useCallback(() => {
    if (open) {
      closeSpellcheck();
    } else {
      void runSpellcheck(text);
    }
  }, [open, text]);

  return (
    <div className='compose-form__spellcheck-button'>
      <IconButton
        icon='spellcheck'
        iconComponent={TextAUnderlineIcon}
        title={intl.formatMessage(open ? messages.close : messages.check)}
        active={open}
        expanded={open}
        disabled={!text.trim()}
        onClick={handleClick}
        className={open ? 'active' : undefined}
        inverted
      />
    </div>
  );
};

const IssueRow: FC<{
  issue: SpellingIssue;
  onReplace: (word: string, replacement: string) => void;
}> = ({ issue, onReplace }) => {
  return (
    <li className={classes.issue}>
      <span className={classes.word}>{issue.word}</span>
      <span className={classes.arrow} aria-hidden>
        →
      </span>
      {issue.suggestions.length > 0 ? (
        <span className={classes.suggestions}>
          {issue.suggestions.map((suggestion) => (
            <SuggestionButton
              key={suggestion}
              word={issue.word}
              suggestion={suggestion}
              onReplace={onReplace}
            />
          ))}
        </span>
      ) : (
        <span className={classes.noSuggestion}>
          <FormattedMessage
            id='spellcheck.no_suggestion'
            defaultMessage='No suggestion'
          />
        </span>
      )}
    </li>
  );
};

const SuggestionButton: FC<{
  word: string;
  suggestion: string;
  onReplace: (word: string, replacement: string) => void;
}> = ({ word, suggestion, onReplace }) => {
  const handleClick = useCallback(() => {
    onReplace(word, suggestion);
  }, [onReplace, word, suggestion]);

  return (
    <button type='button' className={classes.suggestion} onClick={handleClick}>
      {suggestion}
    </button>
  );
};

/** Results of the spelling check, shown under the text area */
export const SpellcheckPanel: FC = () => {
  const dispatch = useAppDispatch();
  const text = useComposeText();
  const current = useSpellcheckState();

  // Close the results once the post is sent or cleared
  useEffect(() => {
    if (!text.trim() && current.status !== 'idle') {
      closeSpellcheck();
    }
  }, [text, current.status]);

  // Results belong to the compose form they were made for: close them when
  // it goes away (e.g. the compose modal is closed)
  useEffect(() => closeSpellcheck, []);

  const handleReplace = useCallback(
    (word: string, replacement: string) => {
      const newText = replaceWord(text, word, replacement);

      dispatch(changeCompose(newText));
      void runSpellcheck(newText);
    },
    [dispatch, text],
  );

  const handleRecheck = useCallback(() => {
    void runSpellcheck(text);
  }, [text]);

  if (current.status === 'idle') {
    return null;
  }

  return (
    <div className={classes.panel} role='status'>
      {current.status === 'loading' && (
        <div className={classes.message}>
          <LoadingIndicator />
          <FormattedMessage
            id='spellcheck.loading'
            defaultMessage='Checking spelling…'
          />
        </div>
      )}

      {current.status === 'error' && (
        <div className={classes.message}>
          <FormattedMessage
            id='spellcheck.error'
            defaultMessage='The spelling check could not be loaded.'
          />
        </div>
      )}

      {current.status === 'done' && (
        <>
          {current.issues.length === 0 ? (
            <div className={classes.message}>
              <FormattedMessage
                id='spellcheck.no_issues'
                defaultMessage='No spelling mistakes found.'
              />
            </div>
          ) : (
            <ul className={classes.issues}>
              {current.issues.map((issue) => (
                <IssueRow
                  key={issue.word}
                  issue={issue}
                  onReplace={handleReplace}
                />
              ))}
            </ul>
          )}

          <div className={classes.footer}>
            <span className={classes.hint}>
              <FormattedMessage
                id='spellcheck.hint'
                defaultMessage='Checks Korean spelling only, not spacing or grammar.'
              />
            </span>

            {current.checkedText !== text && (
              <button
                type='button'
                className={classes.recheck}
                onClick={handleRecheck}
              >
                <FormattedMessage
                  id='spellcheck.recheck'
                  defaultMessage='Check again'
                />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
